import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth, useClerk } from "@clerk/clerk-react";
import { RED, RED_DARK } from "../utils/designTokens";

const FONT_FAMILY =
  '"Plus Jakarta Sans", Inter, system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial, sans-serif';
const BG_GREY = "#F2F2F2";

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { isLoaded: authLoaded, isSignedIn } = useAuth();
  const { openSignIn } = useClerk();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [email, setEmail] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (authLoaded && isSignedIn) {
      navigate("/dashboard", { replace: true });
    }
  }, [authLoaded, isSignedIn, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email.trim()) {
      setError("Email address is required");
      return;
    }

    setLoading(true);
    try {
      openSignIn({
        afterSignInUrl: "/dashboard",
        initialValues: { emailAddress: email } as any,
      });
    } catch (err: any) {
      let msg = "Sign in failed. Please try again.";
      if (err.message) msg = err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#FFFFFF",
      borderRadius: "15px",
      height: 56,
      fontFamily: FONT_FAMILY,
      fontSize: 16,
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused fieldset": { border: `2px solid ${RED}` },
    },
    "& .MuiInputBase-input": {
      fontFamily: FONT_FAMILY,
      fontSize: 16,
    },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: FONT_FAMILY,
        bgcolor: BG_GREY,
      }}
    >
      {/* CENTERED CONTENT WRAPPER */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "1090px",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: "center",
          px: 4,
          gap: 6,
        }}
      >
      {/* LEFT PANEL - Login Form */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          py: isMobile ? 4 : 0,
          bgcolor: BG_GREY,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 360 }}>
        <Box sx={{ mb: 3 }}>
          <img
            src="/worldlinklogo.svg"
            alt="WorldLink"
            style={{ height: 24, width: "auto" }}
          />
        </Box>

        <Typography
          sx={{
            fontFamily: FONT_FAMILY,
            fontWeight: 700,
            fontSize: 24,
            color: RED,
            mb: 2.5,
          }}
        >
          Sign In
        </Typography>

        <Box component="form" onSubmit={handleSubmit}>
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontWeight: 700,
              fontSize: 14,
              color: "#000000",
              mb: 0.5,
            }}
          >
            Email Address:
          </Typography>
          <TextField
            fullWidth
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            sx={{ ...inputSx, mb: 2 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                sx={{
                  color: RED,
                  "&.Mui-checked": { color: RED },
                  borderRadius: "4px",
                }}
              />
            }
            label={
              <Typography
                sx={{
                  fontFamily: FONT_FAMILY,
                  fontWeight: 500,
                  fontSize: 12,
                  color: "#000000",
                }}
              >
                Remember me
              </Typography>
            }
            sx={{ mb: 2, ml: -0.5 }}
          />

          {error && (
            <Typography
              sx={{
                color: "error.main",
                fontSize: 14,
                mb: 2,
                fontFamily: FONT_FAMILY,
              }}
            >
              {error}
            </Typography>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              height: 48,
              borderRadius: "15px",
              background: RED,
              "&:hover": { background: RED_DARK },
              fontFamily: FONT_FAMILY,
              fontWeight: 700,
              fontSize: 16,
              textTransform: "none",
              color: "#F2F2F2",
              boxShadow: "none",
              "&:disabled": {
                background: RED,
                opacity: 0.7,
                color: "#F2F2F2",
              },
            }}
          >
            {loading ? "Signing in..." : "Sign In"}
          </Button>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
            <Typography
              component="span"
              sx={{
                fontFamily: FONT_FAMILY,
                fontWeight: 300,
                fontSize: 12,
                color: "#000000",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Forgot Password
            </Typography>
          </Box>
        </Box>
        </Box>
      </Box>

      {/* RIGHT PANEL- Brand / Welcome */}
      {!isMobile && (
        <Box
          sx={{
            flex: 0.8,
            position: "relative",
            overflow: "hidden",
            borderRadius: "30px",
            my: 8,
            maxHeight: "530px",
            height: "530px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            px: 4,
            py: 4,
            backgroundImage: "url(/worldlink_login_page.png)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {/* WL-lines decorative overlay - right aligned, transparent */}
          <Box
            component="img"
            src="/WL-lines.png"
            alt=""
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              height: "100%",
              width: "auto",
              opacity: 0.25,
              pointerEvents: "none",
              objectFit: "contain",
              objectPosition: "right top",
            }}
          />

          <Box sx={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", flex: 1 }}>
            {/* Smaller top spacer to push content slightly up */}
            <Box sx={{ flex: 0.4 }} />

            {/* Center content */}
            <Typography
              sx={{
                fontFamily: FONT_FAMILY,
                fontWeight: 700,
                fontSize: 24,
                color: "#FFFFFF",
                mb: 2,
                lineHeight: 1.2,
                textAlign: "center",
              }}
            >
              Welcome to WL Supply Chain
            </Typography>

            <Typography
              sx={{
                fontFamily: FONT_FAMILY,
                fontWeight: 400,
                fontSize: 14,
                color: "#FFFFFF",
                lineHeight: 1.7,
                textAlign: "center",
              }}
            >
              The WorldLink Supply Chain Platform unifies route optimization, order
              management, and tariff calculation into one secure, intelligent
              system ,giving enterprises real-time visibility, operational control,
              and consistency across every stage of the logistics lifecycle.
              {" "}
              It enables organizations to optimize transportation routes, streamline
              order workflows, and accurately calculate tariffs helping enterprises
              build efficient, compliant, and scalable supply chain operations.
            </Typography>

            {/* Bottom spacer */}
            <Box sx={{ flex: 0.2 }} />

            {/* Bottom quote pinned to bottom */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 4,
                  height: 28,
                  bgcolor: "#000000",
                  borderRadius: "15px",
                  flexShrink: 0,
                  mt: 0.5,
                }}
              />
              <Typography
                sx={{
                  fontFamily: FONT_FAMILY,
                  fontWeight: 700,
                  fontSize: 20,
                  color: "#FFFFFF",
                  lineHeight: 1.3,
                  textAlign: "left",
                }}
              >
                Strengthen compliance and optimize your supply chain performance
                today.
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
      </Box> {/* end centered wrapper */}
    </Box>
  );
};

export default LoginPage;
